import React, { Suspense } from "react";
import { useDynamicScreenImport } from "@/hooks/useDynamicImports";
import { Loader2 } from "lucide-react";

// List all possible agent states here
export enum AgentState {
  Idle = "idle",
  Loading = "loading",
  Completed = "completed",
  Configuration = "config_required",
  Install = "installation_required",
  Review = "review_required",
  // more states to be added later
}

// Fallback component for unknown states
const UnknownStateScreen: React.FC = () => (
  <div>Unknown State</div>
);

// A mapping from AgentState to the corresponding screen file name convention
const screenFileNameMap: Record<AgentState, string> = {
  [AgentState.Idle]: "TriggerScreen",
  [AgentState.Loading]: "LoadingScreen",
  [AgentState.Completed]: "OutputScreen",
  [AgentState.Configuration]: "ConfigScreen",
  [AgentState.Install]: "InstallScreen",
  [AgentState.Review]: "ReviewScreen",
  // Add more as you define new states and their corresponding screen names
};

// Component that handles dynamic screen loading
interface DynamicScreenLoaderProps {
  agentId: string;
  screenFileName: string;
  state: AgentState;
}

const Loader = () => {
  return (
    <div className="flex p-8 rounded-lg text-card-foreground shadow-sm justify-center items-center">
      <div className="text-center">Loading...</div>
      <div className="flex justify-center items-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
    </div>
  )
};

const DynamicScreenLoader: React.FC<DynamicScreenLoaderProps> = ({ agentId, screenFileName, state }) => {
  console.warn("Agent state passed:", state);
  
  const { screenComponent: ScreenComponent, loading, error, availableModules } = useDynamicScreenImport(agentId, screenFileName);

  if (loading) {
    return <Loader />;
  }

  if (error || !ScreenComponent) {
    console.warn(`Screen not found for agentId: ${agentId}, screenFileName: ${screenFileName}. Available modules:`, availableModules);
    
    // it takes time for the component state to be refreshed so we need to wait for it...
    return <Loader />;
  }

  return <ScreenComponent />;
};

// Renderer function to get the right screen for a state and agentId
export function renderAgentScreen(state: AgentState, agentId: string): React.ReactElement | null {
  const screenFileName = screenFileNameMap[state];

  if (!screenFileName) {
    // If there's no defined file name for the state, render UnknownStateScreen
    return (
      <Suspense fallback={<div className="flex p-8 rounded-lg text-card-foreground shadow-sm justify-center items-center">Loading...</div>}>
        <UnknownStateScreen />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div className="flex p-8 rounded-lg text-card-foreground shadow-sm justify-center items-center">Loading...</div>}>
      <DynamicScreenLoader agentId={agentId} screenFileName={screenFileName} state={state} />
    </Suspense>
  );
}